import React, { Fragment, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TextsContext } from 'context/textsContext';
import { useMyFavoritePlanForm } from "context/MyFavoritePlanFormContext";
import { MY_FAVORITE_PLAN_STEPS, buildFavPlanPath, findFavPlanStepIndex } from "constants/myFavoritePlanForm";
import { TitleH1Component } from "components/TitleH1Component";
import { TitleH3Component } from "components/TitleH3Component";
import Stylegeneral from 'scss/general.module.scss';
import Style from 'scss/step8.module.scss'
import { TitleH2Component } from "components/TitleH2Component";
import Styleboton from "scss/btn.module.scss";
import { Icon } from "@iconify/react";
import { useQuoteFormContext, isFavPlanRoute } from "hooks/useQuoteFormContext";
import styleTexto from 'scss/text.module.scss';
import AccordionComponent from "pages/Step8/component/AccordionComponent";
import { downloadPdfWithData } from "api/pdfGeneratorApi";
import { usePdf } from "hooks/PdfHook";
import { CircularProgress } from "@mui/material";
import { isInFrame, renderWithRegistered } from "helpers/toolsHelper";
import styleForm from 'scss/form.module.scss';
import ValidaComponent from "components/ValidaComponent";
import { convertMoneyAndTruncateOrRound } from "components/utils/helper";
import SaveFavoritePlanDialog from "components/saveFavoritePlan/SaveFavoritePlanDialog";
import { GEO_VALIDATION } from "constants/validationConstants";
import { transformData } from "components/utils/RequestQuoter.util";
import { CalculationErrorBadge } from "components/CalculationErrorBadge";
import { useRecalculateQuote } from "hooks/useRecalculateQuote";

const hasValidPrice = (contextState) =>
    +contextState?.calculation?.data?.quotation?.totalPremiumWithFee > 0;

const QuoterSummaryDetails = () => {
    const { texts } = useContext(TextsContext);
    const { name, surname, coverageShow: coverage } = useQuoteFormContext();
    const { getPdfRefactorData } = usePdf();
    const [loading, setLoading] = React.useState(false);

    const handlePdf = () => {
        if (loading) return;
        setLoading(true);
        downloadPdfWithData({ ...getPdfRefactorData() }, isInFrame())
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Fragment>
            <TitleH2Component styleGroup="raleway fw_400  color_secundary mb-2 ">
                {name} {surname}
            </TitleH2Component>
            <TitleH3Component styleGroup=" pb-2 pb-md-0 ">
                <span
                    className={`color_primary raleway_bold  ${Style.tama_subtitle}`}>{texts?.step8_phrase_date ?? 'Coverage Start Date'}</span>
                <p

                    className={`${Style.tama_date}  raleway color_primary`}>{coverage}</p>
            </TitleH3Component>

            {loading ? <CircularProgress size={50} />
                :
                <button type="button" onClick={handlePdf}
                    className={`${Styleboton.btn} d-flex align-items-center justify-content-center my-3 py-3 py-md-2 mb-1 ${Styleboton.btndescarga} w-100`}>
                    <Icon icon="prime:file-pdf" width={24} height={24} className={`${Style.icono}`} /> <span
                        className={`${Styleboton.text_boton} raleway`}>{texts?.step8_phrase_download ?? 'Download quote'}</span>
                </button>
            }
            <hr className={`${Style.line}`} />
        </Fragment>
    );
};

const FavPlanSummaryActions = () => {
    const { texts, getCurrentLanguage } = useContext(TextsContext);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const {
        goToDashboard,
        contextState,
        favoritePlanId,
        isEditMode,
        favoritePlanDescription,
        getIncompleteModules,
        calculationError,
    } = useMyFavoritePlanForm();
    const { recalculate, isRecalculating } = useRecalculateQuote();
    const [showSaveDialog, setShowSaveDialog] = React.useState(false);
    const currentIndex = findFavPlanStepIndex(pathname);

    // Mismo chequeo que pinta la X de "pendiente" en el sidebar (ver
    // FavoritePlanSidebarSteps/getIncompleteModules): si algún módulo tiene campos
    // obligatorios sin llenar, no se deja guardar un template a medio hacer. El detalle de
    // qué falta ya lo muestra el sidebar - acá solo se bloquea el botón.
    const hasIncompleteSteps = getIncompleteModules().length > 0;
    // El último compute falló o todavía no hay un precio válido calculado (ver
    // CalculationsHook/quoteFormState): guardar acá guardaría un plan con un precio
    // "legacy" respecto a los cambios más recientes del formulario.
    const isPriceStale = calculationError || !hasValidPrice(contextState);

    // Lo que se guarda es el request de `/rate-engine/compute`, para que el plan guardado
    // sea re-cotizable sin reconstruirlo. `transformData` mete el estado del formulario
    // completo en `contextData`, que es de donde se rehidrata el form y se pinta la tabla.
    // Mismo helper y misma firma que usan los pasos para calcular el costo estimado.
    const planData = React.useMemo(
        () => transformData(contextState, getCurrentLanguage(), null),
        [contextState, getCurrentLanguage]
    );

    const backUrl = () => {
        if (currentIndex === 0) {
            goToDashboard();
        } else {
            navigate(buildFavPlanPath(MY_FAVORITE_PLAN_STEPS[currentIndex - 1].slug, favoritePlanId));
        }
    };

    return (
        <Fragment>
            {isPriceStale && (
                <div className="d-flex justify-content-center mb-2">
                    <CalculationErrorBadge onRetry={recalculate} retrying={isRecalculating} variant="card" />
                </div>
            )}
            <div className="d-flex justify-content-center mt-5 mb-4">
                <button type="button" onClick={backUrl} className={`Poppins fw_500 color_secundary border-0 me-4 ${Styleboton.btnNeutro} ${Styleboton.wpbtn}`}>
                    {texts?.footer_button_previous ?? 'Previous'}
                </button>
                <button
                    type="button"
                    onClick={() => setShowSaveDialog(true)}
                    disabled={hasIncompleteSteps || isPriceStale}
                    title={
                        hasIncompleteSteps
                            ? (texts?.favorite_plan_summary_incomplete_tooltip ?? 'Complete all steps before saving')
                            : isPriceStale
                                ? (texts?.favorite_plan_summary_stale_price_tooltip ?? 'Update the price before saving')
                                : undefined
                    }
                    className={`Poppins fw_500 color_primary ${Styleboton.btn} ${Styleboton.wpbtn}`}
                >
                    {isEditMode
                        ? (texts?.favorite_plan_button_update ?? 'Update My Favorite Plan')
                        : (texts?.favorite_plan_button_save ?? 'Save to My Favorite Plans')}
                </button>
            </div>

            {/* Mismo diálogo para crear y editar. En edición llega precargado el nombre
                del plan (editable) y `favoritePlanId` hace que confirme con un PATCH.
                `data` guarda el request de pricing; el estado del form va en `contextData`. */}
            <SaveFavoritePlanDialog
                show={showSaveDialog}
                onClose={() => setShowSaveDialog(false)}
                planData={planData}
                initialName={isEditMode ? (favoritePlanDescription ?? '') : ''}
                favoritePlanId={isEditMode ? favoritePlanId : undefined}
            />
        </Fragment>
    );
};


const SummaryStep = () => {
    const { texts } = useContext(TextsContext);
    const { pathname } = useLocation();
    const isFavPlan = isFavPlanRoute(pathname);
    const { contextState, acceptNonRegion, acceptNonRegion2, handleChecks, calculationError } = useQuoteFormContext();
    const { recalculate, isRecalculating } = useRecalculateQuote();
    const selectedGeo = contextState?.selectedBenefits?.geogrcov;
    const selectedProvider = contextState?.selectedBenefitsIds?.provdnet;
    const hideGeoDisclaimer = selectedGeo === GEO_VALIDATION.WORLDWIDE && selectedProvider === GEO_VALIDATION.PROVIDER_GLOBAL_OPEN;
    const isPriceStale = calculationError || !hasValidPrice(contextState);

    const getCalculation = () => {
        const quoteCalculation = (contextState?.calculation) ? contextState?.calculation : 0
        const totalPremiumWithFee = +quoteCalculation?.data?.quotation?.totalPremiumWithFee || 0
        return convertMoneyAndTruncateOrRound(totalPremiumWithFee.toFixed(2));
    }

    const paragraphstep8 = texts?.step8_title;
    const paragraphgeneral_header_title = texts?.general_header_title;

    return (
        <Fragment>
            <React.Fragment>
                    <section className="pb-4 pb-mb-2" id="sectionscroll">
                        <div className="container ">
                            <div className="d-block d-lg-none">
                                <TitleH2Component
                                    styleGroup={`raleway fw_400 lh_40 color_secundary mb-0 py-2 ${Stylegeneral.titulo_principal}`}>
                                    <p className={Stylegeneral.marca_registradaContainer}>
                                        {renderWithRegistered(paragraphgeneral_header_title)}
                                    </p>

                                </TitleH2Component>
                            </div>
                            <TitleH1Component styleGroup={`raleway_semibold fw_700  lh_30 color_primary pb-3 ${Stylegeneral.titulo}`}>
                                <p className={Stylegeneral.marca_registradaContainer}>
                                    {renderWithRegistered(paragraphstep8)}
                                </p>
                            </TitleH1Component>

                            <TitleH3Component
                                styleGroup={`Poppins fw_400 color_primary  ${Stylegeneral.subtitulo} ${styleTexto.tamasubparrafo}`}>
                                {texts?.step8_phrase_one ?? 'You’ve designed a plan that will provide you with tailor-made coverage to protect your family. Based on your specific selections, here is the annual estimate cost of your custom My VIP Care plan.'}
                            </TitleH3Component>


                        </div>
                    </section>

                    <section>
                        <div className="container">
                            <div className={`${Style.row}`}>
                                <div className={`${Style.col1} me-0 me-md-5`}>

                                    <div className={`${Stylegeneral.cuadro_title_azul}`}>
                                        <TitleH3Component
                                            styleGroup={`Poppins fw_400 color-white mb-0 text-start ${styleTexto.tamasubparrafo}`}>
                                            {texts?.step8_phrase_two ?? 'Do you want to make changes? You can make the changes from the dropdown arrows and adjust your plan to your needs.'}
                                        </TitleH3Component>
                                    </div>
                                    <AccordionComponent />

                                    {!isFavPlan && (
                                    <div className="mt-3">
                                        <div className={`${Stylegeneral.cuadro_beneficio} p-2`}>
                                            <TitleH2Component styleGroup="raleway fw_700 color_primary mb-4 text-shadow">
                                                {texts?.step11_provider_message_title ?? 'Important provider networks message:'}
                                            </TitleH2Component>

                                            {!hideGeoDisclaimer && (
                                            <div className={styleForm.containercheck}>
                                                <div className="form-check mt-4">
                                                    <input className="form-check-input" name="acceptNonRegion" checked={acceptNonRegion} onChange={handleChecks} type="checkbox" value="" id="acepto" />
                                                    <label className={`Poppins typecursiva fw_700 color_primary mb-0 ${styleTexto.h3}`} htmlFor="acepto">
                                                        {texts?.general_dislcaimer_step8 ?? 'Entiendo que he seleccionado una opción de cobertura geográfica y/o red de proveedores con restricciones en relación con las regiones y/o los hospitales en donde mi plan tendrá cobertura'}
                                                    </label>
                                                </div>
                                                <div className={`${styleForm.validador}`}>
                                                    <ValidaComponent keyName={"acceptNonRegion"} styleGroup={`${styleForm.valida} ${styleForm.validaSpecial}`} />
                                                </div>
                                            </div>
                                            )}

                                            <div className={styleForm.containercheck}>
                                                <div className="form-check mt-4">
                                                    <input className="form-check-input" type="checkbox" value="" id="acepto2" name="acceptNonRegion2" checked={acceptNonRegion2} onChange={handleChecks} />
                                                    <label className={`Poppins typecursiva fw_700 color_primary mb-0 ${styleTexto.h3}`} htmlFor="acepto2">
                                                        {texts?.general_dislcaimer_2_step8 ?? 'Confirmo que he revisado y entendido todos los beneficios que ofrece la póliza “My VIP Care” de VUMI® y he elegido aquellos que requiero y declinado otros. Por lo tanto, entiendo y acepto el alcance de la cobertura del plan diseñado y elegido por mí.'}
                                                    </label>
                                                </div>
                                                <div className={`${styleForm.validador}`}>
                                                    <ValidaComponent keyName={"acceptNonRegion2"} styleGroup={`${styleForm.valida}  `} />
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                    )}

                                </div>

                                <div className={`${Style.col2} mb-5 mb-lg-0`}>
                                    <div className={`${Style.tarjeta_info} `}>
                                        <TitleH3Component styleGroup="raleway_bold fw_700  color_primary mb-3 mb-md-2">
                                            {texts?.step8_phrase_summary ?? 'Summary'}
                                        </TitleH3Component>
                                        {!isFavPlan && <QuoterSummaryDetails />}
                                        <TitleH2Component styleGroup="raleway fw_400  color_primary_2 ">
                                            {texts?.step8_phrase_cost ?? 'Estimated annual cost:'}
                                        </TitleH2Component>
                                        <TitleH1Component styleGroup="raleway fw_600 color_secundary">
                                            {getCalculation()}
                                        </TitleH1Component>
                                        {isPriceStale && (
                                            <CalculationErrorBadge onRetry={recalculate} retrying={isRecalculating} variant="card" />
                                        )}
                                    </div>

                                </div>
                            </div>
                            {isFavPlan && <FavPlanSummaryActions />}
                        </div>
                    </section>
                </React.Fragment>
        </Fragment >
    )
}
export default SummaryStep;
